import './KLZipFileViewer.css'

import { CopyFilled, CopyTwoTone } from '@ant-design/icons'
import { useAuth0 } from '@auth0/auth0-react'
import { Intent } from '@blueprintjs/core'
import { Button, Select, Spin } from 'antd'
import { Checkbox } from 'antd'
import { Colors } from 'common/colors'
import { Spacing } from 'common/stylings'
import KLIndivCSVViewer from 'components/CSVViewer/KLIndivCSVViewer'
import Stack from 'components/Stack'
import Toaster from 'components/Toaster'
import Text from 'components/Typography'
import dicomParser from 'dicom-parser'
import JSZip from 'jszip'
import { get } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import { getAllDoctors } from 'store/doctor/selectors'
import { RootState } from 'store/rootReducer'
import {
  createLoadingSelector,
  createSuccessSelector,
} from 'store/status/selectors'
import {
  uploadDicomAsMP4,
  uploadDicomsToEncord,
  uploadDicomsToFlexView,
} from 'store/videoSegments/actionCreators'
import {
  addVideoImportLogFlow,
  editVideoCountFlow,
  uploadDicomAsMP4Flow,
  uploadDicomsToEncordFlow,
  uploadDicomsToFlexViewFlow,
} from 'store/videoSegments/actions'
import { extractTarEntries } from 'utilities/tar'

type Props = KLZipFileViewerProps &
  StoreProps<typeof mapStateToProps, typeof mapDispatchToProps>

interface KLZipFileViewerProps {
  url: string
  video: any
  csvURL: string
  toggleVisible: any
  reportCSVURL: string
}

export interface FileItem {
  type: 'file' | 'directory'
  name: string
  path: string
  seriesDescription?: string
  patientName?: string
  patientID?: string
  studyDate?: string
  studyDescription?: string
  accessionNumber?: string
  seriesNumber?: string
  numStudyInstances?: string
  modality?: string
  children?: FileItem[]
  selected?: boolean
  fullPath: string
}

const KLZipFileViewer: React.FC<Props> = ({
  url,
  video,
  uploadDicomAsMP4,
  isUploadingDicomAsMP4,
  csvURL,
  reportCSVURL,
  isUploadedDicomAsMP4,
  editVideoCount,
  addVideoImportLog,
  allDoctors,
  uploadDicomsToEncord,
  isUploadingDicomsToEncord,
  isUploadedDicomsToEncord,
  toggleVisible,
  isUploadingDicomsToFlexView,
  isUploadedDicomsToFlexView,
  uploadDicomsToFlexView,
}) => {
  const [hierarchy, setHierarchy] = useState<FileItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPaths, setSelectedPaths] = useState<string[]>([])
  const [commonSeriesDescription, setCommonSeriesDescription] =
    useState<string>('')
  const exportButtonSuperAnnotateRef = useRef(false)
  const exportButtonFlexViewRef = useRef(false)
  const exportButtonEncordRef = useRef(false)
  const [superAnnotateLogPayload, setSuperAnnotateLogPayload] = useState<any>(
    [],
  )
  const [flexViewLogPayload, setFlexViewLogPayload] = useState<any>([])
  const [encordLogPayload, setEncordLogPayload] = useState<any>([])
  const [
    encordLogPayloadForRadiologyReportText,
    setEncordLogPayloadForRadiologyReportText,
  ] = useState<any>([])
  const { user } = useAuth0()
  const [selectedStudyMetadata, setSelectedStudyMetadata] =
    useState<Partial<FileItem> | null>(null)
  const [radiologyReportText, setRadiologyReportText] = useState<string>('')

  function formatDate(dateString: string): string {
    if (!dateString || dateString.length !== 8) return 'Invalid Date'

    const year = dateString.substring(0, 4)
    const month = dateString.substring(4, 6)
    const day = dateString.substring(6, 8)

    const date = new Date(`${year}-${month}-${day}`)

    if (isNaN(date.getTime())) return 'Invalid Date'

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }

    return date
      .toLocaleDateString('en-US', options)
      .replace(',', '')
      .replace(/ /g, '-')
  }

  const getBaseUri = (uri: string) => {
    const lastIndex = uri.lastIndexOf('/')
    return uri.substring(0, lastIndex + 1)
  }

  const toggleDirectorySelection = (path: string) => {
    const baseUri = getBaseUri(video.uri)

    setHierarchy(prev => {
      let newState = false
      const newSelectedPaths: string[] = []
      let commonDescription = ''
      let metadata: Partial<FileItem> | null = null

      const findNewState = (items: FileItem[]): boolean => {
        for (const item of items) {
          if (item.path === path) {
            newState = !item.selected
            if (item.seriesDescription && !newState) {
              commonDescription = ''
            } else if (item.seriesDescription && newState) {
              commonDescription = item.seriesDescription
            }

            if (newState) {
              metadata = {
                patientName: item.patientName,
                patientID: item.patientID,
                studyDate: item.studyDate
                  ? formatDate(item.studyDate)
                  : undefined,
                studyDescription: item.studyDescription,
                accessionNumber: item.accessionNumber,
                seriesNumber: item.seriesNumber,
                numStudyInstances: item.numStudyInstances,
                modality: item.modality,
                seriesDescription: item.seriesDescription,
              }
            }
            return true
          }
          if (item.children && findNewState(item.children)) {
            return true
          }
        }
        return false
      }

      findNewState(prev)

      const applyNewState = (items: FileItem[]): FileItem[] =>
        items.map(item => {
          const shouldApplyNewState =
            item.path === path || item.path.startsWith(path + '/')
          const isSelected = shouldApplyNewState ? newState : false

          if (item.type === 'file' && isSelected) {
            newSelectedPaths.push(`${baseUri}${item.fullPath}`)
            if (!commonDescription && item.seriesDescription) {
              commonDescription = item.seriesDescription
            }
          }

          return {
            ...item,
            selected: isSelected,
            children: item.children ? applyNewState(item.children) : undefined,
          }
        })

      const updatedHierarchy = applyNewState(prev)
      setSelectedPaths(newSelectedPaths)

      if (commonDescription) {
        commonDescription = commonDescription
          .replace(/\s(?!\s*$)/g, '-')
          .trimEnd()
      }

      setCommonSeriesDescription(
        newSelectedPaths.length > 0 ? commonDescription : '',
      )

      setSelectedStudyMetadata(newState ? metadata : null)

      return updatedHierarchy
    })
  }

  const renderCheckbox = (item: FileItem) => (
    <Checkbox
      style={{ marginRight: '8px' }}
      checked={item.selected || false}
      onChange={() => toggleDirectorySelection(item.path)}
    />
  )

  React.useEffect(() => {
    if (exportButtonSuperAnnotateRef.current && isUploadedDicomAsMP4) {
      editVideoCount({
        videoId: video._id,
        export_count: video.export_count + 1,
        batch: video.batch,
      })
      addVideoImportLog(superAnnotateLogPayload)
      setSuperAnnotateLogPayload([])
      exportButtonSuperAnnotateRef.current = false
    }
  }, [isUploadedDicomAsMP4])

  // React.useEffect(() => {
  //   if (exportButtonFlexViewRef.current && isUploadedDicomsToFlexView) {
  //     editVideoCount({
  //       videoId: video._id,
  //       export_count: video.export_count + 1,
  //       batch: video.batch,
  //     })
  //     addVideoImportLog(flexViewLogPayload)
  //     setFlexViewLogPayload([])
  //     exportButtonFlexViewRef.current = false
  //   }
  // }, [isUploadedDicomsToFlexView])

  function propagateMetadataUp(items: FileItem[]): FileItem[] {
    return items.map(item => {
      if (
        item.type === 'directory' &&
        item.children &&
        item.children.length > 0
      ) {
        const newChildren = propagateMetadataUp(item.children)

        const aggregated: FileItem = { ...item }

        for (const child of newChildren) {
          aggregated.seriesDescription =
            aggregated.seriesDescription || child.seriesDescription
          aggregated.patientName = aggregated.patientName || child.patientName
          aggregated.patientID = aggregated.patientID || child.patientID
          aggregated.studyDate = aggregated.studyDate || child.studyDate
          aggregated.studyDescription =
            aggregated.studyDescription || child.studyDescription
          aggregated.accessionNumber =
            aggregated.accessionNumber || child.accessionNumber
          aggregated.seriesNumber =
            aggregated.seriesNumber || child.seriesNumber
          aggregated.modality = aggregated.modality || child.modality

          const currentNum = parseInt(aggregated.numStudyInstances || '0', 10)
          const childNum = parseInt(child.numStudyInstances || '0', 10)
          if (childNum > currentNum) {
            aggregated.numStudyInstances = child.numStudyInstances
          }
        }

        return { ...aggregated, children: newChildren }
      }
      return item
    })
  }

  React.useEffect(() => {
    if (exportButtonEncordRef.current && isUploadedDicomsToEncord) {
      editVideoCount({
        videoId: video._id,
        export_count: video.export_count + 1,
        batch: video.batch,
      })
      addVideoImportLog(encordLogPayload)
      addVideoImportLog(encordLogPayloadForRadiologyReportText)
      setEncordLogPayload([])
      setEncordLogPayloadForRadiologyReportText([])
      exportButtonEncordRef.current = false
    }
  }, [isUploadedDicomsToEncord])

  // recursively consolidate directories with a single child
  const consolidateDirectories = (items: FileItem[]): FileItem[] => {
    return items.map(item => {
      if (item.type === 'directory' && item.children?.length === 1) {
        let currentItem = item
        while (
          currentItem.children?.length === 1 &&
          currentItem.children[0].type === 'directory'
        ) {
          const child = currentItem.children[0]
          currentItem = {
            ...child,
            name: `${currentItem.name}/${child.name}`,
            seriesDescription:
              child.seriesDescription || currentItem.seriesDescription,
          }
        }

        return {
          ...currentItem,
          children: currentItem.children
            ? consolidateDirectories(currentItem.children)
            : [],
        }
      }

      if (item.children) {
        return {
          ...item,
          children: consolidateDirectories(item.children),
        }
      }

      return item
    })
  }

  useEffect(() => {
    const fetchZipFile = async () => {
      try {
        setIsLoading(true)
        const response = await fetch(url)
        const blob = await response.blob()
        const arrayBuffer = await blob.arrayBuffer()
        const zip = await JSZip.loadAsync(arrayBuffer)

        // Handle Gradient Health tar-wrapped dicomweb format
        await extractTarEntries(zip)

        const buildHierarchy = async (zip: JSZip): Promise<FileItem[]> => {
          const items: FileItem[] = []
          const folderMap: Record<string, FileItem> = {}

          const promises: Promise<void>[] = []

          zip.forEach((relativePath, file) => {
            // Only display DICOM files
            if (file.name.startsWith('.') || !file.name.endsWith('.dcm')) {
              return
            }

            let parts = relativePath.split('/')

            const fullPath = parts.join('/')

            // Handle new dicomweb structure: dicomweb/studies/[studyUID]/series/[seriesUID]/instances/[instanceUID].dcm
            const dicomwebIndex = parts.findIndex(part => part === 'dicomweb')

            if (dicomwebIndex >= 0) {
              // New structure: start from dicomweb
              parts = parts.slice(dicomwebIndex)
            } else {
              // Legacy structure: check for 'deid' folder (Gradient Health legacy)
              const deidIndex = parts.findIndex(part => part === 'deid')
              if (deidIndex >= 0) {
                parts = parts.slice(deidIndex)
              } else {
                // If neither dicomweb nor deid is present, keep full path (Segmed)
                parts = parts.slice(0)
              }
            }

            let seriesDescription = ''
            let patientName = ''
            let patientID = ''
            let studyDate = ''
            let studyDescription = ''
            let accessionNumber = ''
            let seriesNumber = ''
            let modality = ''

            if (!file.dir && file.name.endsWith('.dcm')) {
              const promise = file.async('arraybuffer').then(fileContent => {
                const dataSet = dicomParser.parseDicom(
                  new Uint8Array(fileContent),
                )

                const tagList = {
                  x0008103e: 'Series Description',
                  x00100010: 'Patient Name',
                  x00100020: 'Patient ID',
                  x00080020: 'Study Date',
                  x00081030: 'Study Description',
                  x00080050: 'Accession Number',
                  x00200011: 'Series Number',
                  x00080060: 'Modality',
                }

                const decodedValues = {}

                Object.entries(tagList).forEach(([tag, description]) => {
                  const element = dataSet.elements[tag]

                  if (element) {
                    const rawValue = dataSet.byteArray.slice(
                      element.dataOffset,
                      element.dataOffset + element.length,
                    )
                    const decodedValue = new TextDecoder().decode(rawValue)
                    decodedValues[tag] = decodedValue
                  } else {
                    decodedValues[tag] = ''
                  }
                })

                seriesDescription = decodedValues['x0008103e'] || ''
                patientName = decodedValues['x00100010'] || ''
                patientID = decodedValues['x00100020'] || ''
                studyDate = decodedValues['x00080020'] || ''
                studyDescription = decodedValues['x00081030'] || ''
                accessionNumber = decodedValues['x00080050'] || ''
                seriesNumber = decodedValues['x00200011'] || ''
                modality = decodedValues['x00080060'] || ''

                parts.reduce((acc, part, index) => {
                  const currentPath = parts.slice(0, index + 1).join('/')
                  const isFile = index === parts.length - 1 && !file.dir
                  if (!acc[currentPath]) {
                    const newItem: FileItem = {
                      type: isFile ? 'file' : 'directory',
                      name: part,
                      path: currentPath,
                      fullPath: fullPath,
                      seriesDescription: isFile
                        ? seriesDescription
                        : acc[currentPath]?.seriesDescription,
                      patientName: isFile
                        ? patientName
                        : acc[currentPath]?.patientName,
                      patientID: isFile
                        ? patientID
                        : acc[currentPath]?.patientID,
                      studyDate: isFile
                        ? studyDate
                        : acc[currentPath]?.studyDate,
                      studyDescription: isFile
                        ? studyDescription
                        : acc[currentPath]?.studyDescription,
                      accessionNumber: isFile
                        ? accessionNumber
                        : acc[currentPath]?.accessionNumber,
                      seriesNumber: isFile
                        ? seriesNumber
                        : acc[currentPath]?.seriesNumber,
                      numStudyInstances:
                        acc[currentPath]?.numStudyInstances || '1',
                      modality: isFile ? modality : acc[currentPath]?.modality,
                      children: isFile ? undefined : [],
                    }
                    if (index === 0) {
                      items.push(newItem)
                    } else {
                      acc[parts.slice(0, index).join('/')].children?.push(
                        newItem,
                      )
                    }
                    acc[currentPath] = newItem
                  } else if (!isFile) {
                    if (!acc[currentPath].seriesDescription) {
                      acc[currentPath].seriesDescription = seriesDescription
                    }
                    if (!acc[currentPath].patientName) {
                      acc[currentPath].patientName = patientName
                    }
                    if (!acc[currentPath].patientID) {
                      acc[currentPath].patientID = patientID
                    }
                    if (!acc[currentPath].studyDate) {
                      acc[currentPath].studyDate = studyDate
                    }
                    if (!acc[currentPath].studyDescription) {
                      acc[currentPath].studyDescription = studyDescription
                    }
                    if (!acc[currentPath].accessionNumber) {
                      acc[currentPath].accessionNumber = accessionNumber
                    }
                    if (!acc[currentPath].seriesNumber) {
                      acc[currentPath].seriesNumber = seriesNumber
                    }
                    if (!acc[currentPath].modality) {
                      acc[currentPath].modality = modality
                    }

                    acc[currentPath].numStudyInstances = (
                      parseInt(acc[currentPath].numStudyInstances || '1', 10) +
                      1
                    ).toString()
                  }
                  return acc
                }, folderMap)
              })
              promises.push(promise)
            } else {
              parts.reduce((acc, part, index) => {
                const currentPath = parts.slice(0, index + 1).join('/')
                const isFile = index === parts.length - 1 && !file.dir
                if (!acc[currentPath]) {
                  const newItem: FileItem = {
                    type: isFile ? 'file' : 'directory',
                    name: part,
                    path: currentPath,
                    fullPath: fullPath,
                    children: isFile ? undefined : [],
                  }
                  if (index === 0) {
                    items.push(newItem)
                  } else {
                    acc[parts.slice(0, index).join('/')].children?.push(newItem)
                  }
                  acc[currentPath] = newItem
                }
                return acc
              }, folderMap)
            }
          })

          await Promise.all(promises)
          return items
        }

        const hierarchy = await buildHierarchy(zip)

        let adjustedHierarchy = hierarchy

        // For new Gradient Health data with dicomweb structure
        if (hierarchy.length === 1 && hierarchy[0].name === 'dicomweb') {
          adjustedHierarchy = hierarchy[0].children || []
        }
        // For legacy Gradient Health data with deid structure
        else if (hierarchy.length === 1 && hierarchy[0].name === 'deid') {
          adjustedHierarchy = hierarchy[0].children || []
        }

        setHierarchy(
          propagateMetadataUp(consolidateDirectories(adjustedHierarchy)),
        )
        setIsLoading(false)
      } catch (error) {
        console.error('Error reading zip file:', error)
        setIsLoading(false)
      }
    }

    fetchZipFile()
  }, [url])

  const renderFiles = (files: FileItem[]) => {
    const sortedFiles = files.sort((a, b) =>
      a.name.localeCompare(
        b.name,
        navigator.languages[0] || navigator.language,
        {
          numeric: true,
          ignorePunctuation: true,
          sensitivity: 'base',
        },
      ),
    )

    return sortedFiles.map((file, index) => (
      <div key={file.path} className="file-item">
        {index + 1}. {file.name}
      </div>
    ))
  }

  const renderDirectories = (
    directories: FileItem[],
    parentIndex: string,
    studyUID: string,
  ) => {
    const extractPrefixFromStudyUID = (fullStudyUID: string): string => {
      if (!fullStudyUID) return ''
      const parts = fullStudyUID.split('.')
      if (parts.length > 8) {
        return parts.slice(0, 8).join('.') + '.'
      }
      return ''
    }

    const commonPrefix = extractPrefixFromStudyUID(studyUID)

    const sortedDirectories = directories.sort((a, b) =>
      a.name.localeCompare(
        b.name,
        navigator.languages[0] || navigator.language,
        {
          numeric: true,
          ignorePunctuation: true,
          sensitivity: 'base',
        },
      ),
    )

    return sortedDirectories.map((directory, dirIndex) => {
      const currentDirIndex = `${parentIndex}.${dirIndex + 1}`

      const isLikelyUID = /^\d+$/.test(directory.name)

      return (
        <div key={directory.path} className="directory">
          <div className="directory-header">
            {!video.uri.includes('-batch') && renderCheckbox(directory)}
            <strong>
              {currentDirIndex}. {}
              {commonPrefix && isLikelyUID && <span>{commonPrefix}</span>}
              <span>{directory.name}</span>
            </strong>
            {directory.seriesDescription && (
              <span> ({directory.seriesDescription})</span>
            )}
          </div>
          <div className="directory-content">
            {directory.children &&
              renderFiles(
                directory.children.filter(item => item.type === 'file'),
              )}
            {directory.children &&
              renderDirectories(
                directory.children.filter(item => item.type === 'directory'),
                currentDirIndex,
                studyUID,
              )}
          </div>
        </div>
      )
    })
  }

  const [clickCopyURI, setClickCopyURI] = useState(false)

  const renderCopyURIButton = () => {
    const toggleAfterCopy = () => {
      setClickCopyURI(true)
      if (video.uri) {
        navigator.clipboard.writeText(video.uri)
        Toaster.show({
          icon: 'tick',
          intent: Intent.SUCCESS,
          message: 'Copied to Clipboard!',
        })
      }
    }
    return !clickCopyURI ? (
      <CopyTwoTone
        style={{ marginLeft: '5px' }}
        onClick={() => toggleAfterCopy()}></CopyTwoTone>
    ) : (
      <CopyFilled style={{ marginLeft: '5px' }}> </CopyFilled>
    )
  }

  const [clickCopyPSLink, setClickCopyPSLink] = useState(false)

  const renderCopyPSLinkButton = () => {
    const toggleAfterCopy = () => {
      setClickCopyPSLink(true)
      if (url) {
        navigator.clipboard.writeText(url)
        Toaster.show({
          icon: 'tick',
          intent: Intent.SUCCESS,
          message: 'Copied to Clipboard!',
        })
        setTimeout(() => {
          const viewerUrl = 'https://www.imaios.com/en/IMAIOS-DICOM-Viewer'
          window.open(viewerUrl, '_blank')
        }, 1200)
      }
    }

    return !clickCopyPSLink ? (
      <CopyTwoTone
        style={{ marginLeft: '5px' }}
        onClick={() => toggleAfterCopy()}></CopyTwoTone>
    ) : (
      <CopyFilled style={{ marginLeft: '5px' }}> </CopyFilled>
    )
  }

  const [isDownloaded, setIsDownloaded] = useState(false)

  const handleDownload = () => {
    if (!isDownloaded) {
      editVideoCount({
        videoId: video._id,
        download_count: video.download_count + 1,
        batch: video.batch,
      })
      setIsDownloaded(true)
    }
  }

  const renderDownloadButton = () => {
    return (
      <div
        style={{
          marginTop: '10px',
        }}>
        <Stack justifyContent="flex-start">
          <a
            href={url}
            download
            style={{ textDecoration: 'none' }}
            onClick={e => {
              if (isDownloaded) {
                e.preventDefault()
              } else {
                handleDownload()
              }
            }}>
            <Button
              disabled={isDownloaded}
              type="primary"
              style={{ marginBottom: '0.5rem' }}>
              Download Zip File
            </Button>
          </a>
        </Stack>
      </div>
    )
  }

  const handleUploadAndExport = (
    selectedPaths: string[],
    destinationProjectSuperAnnotate: string,
  ) => {
    const videoRegion =
      video.region != null && video.region.trim() !== ''
        ? video.region
        : 'No region specified'
    const doctorID = video.doctor
    const videoDoctorEmail = get(allDoctors[doctorID], 'email', '')
    const userEmail = user?.name ?? 'No email specified'
    const dateTime = new Date().toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
    })
    const timeOfImport = dateTime.toString()

    const dateNow = new Date().toISOString()
    const slicedDate = dateNow.slice(0, -1)
    const uploadDate = slicedDate.concat('000Z')

    function extractVideoName(s3Urls: string | any[]) {
      if (!s3Urls || s3Urls.length === 0) {
        throw new Error('The input array is empty')
      }
      const s3Url = s3Urls[0]
      const parts = s3Url.split('/')
      const videoNameWithoutExt = parts[parts.length - 2]
      const videoName = `${videoNameWithoutExt}.mp4`

      return videoName
    }

    let videoName = extractVideoName(selectedPaths)

    if (commonSeriesDescription && commonSeriesDescription.trim() !== '') {
      videoName = videoName.replace('.mp4', `_${commonSeriesDescription}.mp4`)
    }

    const logPayload = {
      videoName,
      videoDoctorEmail,
      userEmail,
      timeOfImport,
      uploadDate,
      videoRegion,
    }
    setSuperAnnotateLogPayload(logPayload)

    const videoTags = [
      ...(video.tags || []),
      ...(video.classificationTags || []),
    ]

    const videoDoctor = get(allDoctors[doctorID], 'fullname', '')

    uploadDicomAsMP4(
      selectedPaths,
      destinationProjectSuperAnnotate,
      commonSeriesDescription,
      videoTags,
      videoDoctor,
      videoRegion,
    )
    exportButtonSuperAnnotateRef.current = true
  }

  const handleUploadToFlexView = (
    selectedPaths: string[],
    metadata: Partial<FileItem> | null,
  ) => {
    const videoRegion =
      video.region != null && video.region.trim() !== ''
        ? video.region
        : 'No region specified'
    const doctorID = video.doctor
    const videoDoctorEmail = get(allDoctors[doctorID], 'email', '')
    const userEmail = user?.name ?? 'No email specified'
    const dateTime = new Date().toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
    })
    const timeOfImport = dateTime.toString()

    const dateNow = new Date().toISOString()
    const slicedDate = dateNow.slice(0, -1)
    const uploadDate = slicedDate.concat('000Z')

    function extractVideoName(s3Urls: string | any[]) {
      if (!s3Urls || s3Urls.length === 0) {
        throw new Error('The input array is empty')
      }
      const s3Url = s3Urls[0]
      const parts = s3Url.split('/')
      const videoNameWithoutExt = parts[parts.length - 2]
      const videoName = `${videoNameWithoutExt}.mp4`

      return videoName
    }

    let videoName = extractVideoName(selectedPaths)

    if (commonSeriesDescription && commonSeriesDescription.trim() !== '') {
      videoName = videoName.replace('.mp4', `_${commonSeriesDescription}.mp4`)
    }

    const logPayload = {
      videoName,
      videoDoctorEmail,
      userEmail,
      timeOfImport,
      uploadDate,
      videoRegion,
    }
    setFlexViewLogPayload(logPayload)

    uploadDicomsToFlexView(selectedPaths, metadata)
    // exportButtonFlexViewRef.current = true
  }

  const handleExportToEncord = async (
    selectedPaths: string[],
    destinationFolderEncord: string,
  ) => {
    const videoRegion =
      video.region != null && video.region.trim() !== ''
        ? video.region
        : 'No region specified'
    const doctorID = video.doctor
    const videoDoctorEmail = get(allDoctors[doctorID], 'email', '')
    const userEmail = user?.name ?? 'No email specified'
    const dateTime = new Date().toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
    })
    const timeOfImport = dateTime.toString()

    const dateNow = new Date().toISOString()
    const slicedDate = dateNow.slice(0, -1)
    const uploadDate = slicedDate.concat('000Z')

    function extractVideoName(s3Urls: string | any[]) {
      if (!s3Urls || s3Urls.length === 0) {
        throw new Error('The input array is empty')
      }
      const s3Url = s3Urls[0]
      const parts = s3Url.split('/')
      const videoNameWithoutExt = parts[parts.length - 2]
      const videoName = `${videoNameWithoutExt}.mp4`

      return videoName
    }

    let videoName = extractVideoName(selectedPaths)

    if (commonSeriesDescription && commonSeriesDescription.trim() !== '') {
      videoName = videoName.replace('.mp4', `_${commonSeriesDescription}.dcm`)
    }

    const logPayload = {
      videoName,
      videoDoctorEmail,
      userEmail,
      timeOfImport,
      uploadDate,
      videoRegion,
    }

    const logPayloadForRadiologyReportText = {
      videoName: videoName.replace('.dcm', '_radiology_report.txt'),
      videoDoctorEmail,
      userEmail,
      timeOfImport,
      uploadDate,
      videoRegion,
    }

    setEncordLogPayload(logPayload)
    setEncordLogPayloadForRadiologyReportText(logPayloadForRadiologyReportText)

    const videoTags = [
      ...(video.tags || []),
      ...(video.classificationTags || []),
    ]

    const videoDoctor = get(allDoctors[doctorID], 'fullname', '')

    uploadDicomsToEncord(
      selectedPaths,
      destinationFolderEncord,
      commonSeriesDescription,
      videoTags,
      videoDoctor,
      videoRegion,
      radiologyReportText,
    )

    exportButtonEncordRef.current = true
  }

  const [destinationProjectSuperAnnotate, setDestinationProjectSuperAnnotate] =
    useState('Select Project')

  const [destinationFolderEncord, setDestinationFolderEncord] =
    useState('Select Folder')

  const renderDestProjectsSuperAnnotate = () => {
    const selectedDestProject = String(destinationProjectSuperAnnotate)

    const { Option } = Select

    const handleChange = (value: string) => {
      setDestinationProjectSuperAnnotate(value)
    }

    return (
      <Stack
        style={{
          paddingRight: 8,
          paddingTop: 0,
          paddingBottom: 10,
        }}>
        <Stack style={{ paddingTop: 5, paddingRight: 8 }}>
          Destination Projects in Super Annotate:
        </Stack>
        <Select
          defaultValue={selectedDestProject}
          style={{ width: 250 }}
          onChange={handleChange}>
          <Option value="Knee MRI projects">Knee MRI projects</Option>
          <Option value="Shoulder MRI projects">Shoulder MRI projects</Option>
          <Option value="Hip MRI projects">Hip MRI projects</Option>
        </Select>
      </Stack>
    )
  }

  const renderDestFoldersEncord = () => {
    const selectedDestFolder = String(destinationFolderEncord)

    const { Option } = Select

    const handleChange = (value: string) => {
      setDestinationFolderEncord(value)
    }

    return (
      <Stack
        style={{
          paddingRight: 8,
          paddingTop: 0,
          paddingBottom: 10,
        }}>
        <Stack style={{ paddingTop: 5, paddingRight: 8 }}>
          Destination Folders in Encord:
        </Stack>
        <Select
          defaultValue={selectedDestFolder}
          style={{ width: 250 }}
          onChange={handleChange}>
          <Option value="Knee MRI folder">Knee MRI folder</Option>
          <Option value="Shoulder MRI folder">Shoulder MRI folder</Option>
          <Option value="Hip MRI folder">Hip MRI folder</Option>
        </Select>
      </Stack>
    )
  }

  return (
    <Stack vertical gutter={Spacing.MEDIUM}>
      <Stack vertical>
        <Text
          style={{
            marginTop: '15px',
            fontSize: '18px',
          }}
          fontSize={16}
          fontWeight="bold">
          {video.filename}
        </Text>
        <Text
          style={{
            marginBottom: '4px',
            fontSize: '12.5px',
          }}
          color={Colors.PALE_GREY}
          fontSize={12}>
          {video.uri}
          {renderCopyURIButton()}
        </Text>
        <Text
          style={{
            marginBottom: '4px',
            fontSize: '12.5px',
          }}
          color={Colors.PALE_GREY}
          fontSize={12}>
          {`Copy presigned URL and open IMAIOS viewer in a separate tab. Paste the copied URL in the 'Import from url' button.`}{' '}
          {renderCopyPSLinkButton()}
          {renderDownloadButton()}
        </Text>

        {/* render KLIndivCSVViewer for individual studies, not main .zip files */}
        {csvURL && !video.uri.includes('-batch') && (
          <div
            style={{
              marginBottom: '10px',
            }}>
            <KLIndivCSVViewer
              url={csvURL}
              reportCSVURL={reportCSVURL}
              studyName={video.filename.split('.').slice(0, -1).join('.')}
              onRadiologyReportTextChange={setRadiologyReportText}
            />
          </div>
        )}

        <div className="zip-viewer">
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Spin />
              <span style={{ marginLeft: '16px' }}>
                Loading Dicom files hierarchy...
              </span>
            </div>
          ) : (
            <div>
              {/* {!video.uri.includes('-batch') &&
                renderDestProjectsSuperAnnotate()} */}
              {!video.uri.includes('-batch') && (
                <div
                  style={{
                    display: 'block',
                    marginBottom: '10px',
                  }}>
                  {/* <div style={{ marginBottom: '10px' }}>
                    <Button
                      type="primary"
                      onClick={() =>
                        handleUploadAndExport(
                          selectedPaths,
                          destinationProjectSuperAnnotate,
                        )
                      }
                      disabled={
                        isUploadingDicomAsMP4 ||
                        isUploadingDicomsToFlexView ||
                        !(
                          selectedPaths.length > 0 &&
                          destinationProjectSuperAnnotate !== 'Select Project'
                        )
                      }>
                      Upload Dicoms to Super Annotate
                    </Button>
                    {isUploadingDicomAsMP4 && (
                      <div style={{ marginLeft: 16, marginTop: 16 }}>
                        <Spin tip="Uploading Dicoms as MP4..." />
                      </div>
                    )}
                  </div> */}
                  {/* <div>
                    <Button
                      type="primary"
                      onClick={() =>
                        handleUploadToFlexView(
                          selectedPaths,
                          selectedStudyMetadata,
                        )
                      }
                      disabled={
                        isUploadingDicomAsMP4 ||
                        isUploadingDicomsToFlexView ||
                        !(selectedPaths.length > 0)
                      }>
                      Upload Dicoms to Flex View Dicom Viewer
                    </Button>
                    {isUploadingDicomsToFlexView && (
                      <div style={{ marginLeft: 16, marginTop: 16 }}>
                        <Spin tip="Uploading Dicoms to Flex View..." />
                      </div>
                    )}
                  </div> */}
                </div>
              )}
              {!video.uri.includes('-batch') && renderDestFoldersEncord()}
              {!video.uri.includes('-batch') && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}>
                  <Button
                    type="primary"
                    onClick={() =>
                      handleExportToEncord(
                        selectedPaths,
                        destinationFolderEncord,
                      )
                    }
                    disabled={
                      isUploadingDicomsToEncord ||
                      !(
                        selectedPaths.length > 0 &&
                        destinationFolderEncord !== 'Select Folder'
                      )
                    }>
                    Upload Dicoms to Encord
                  </Button>
                  {isUploadingDicomsToEncord && (
                    <div style={{ marginLeft: 16 }}>
                      <Spin tip="Uploading Dicoms to Encord..." />
                    </div>
                  )}
                </div>
              )}
              {hierarchy.map((mainDirectory, index) => {
                const hasShortening = mainDirectory.children?.some(child =>
                  child.children?.some(
                    file =>
                      file.type === 'file' && /^\d{10}\.dcm$/.test(file.name),
                  ),
                )

                return (
                  <div key={mainDirectory.path} className="primary-directory">
                    <div className="primary-directory-header">
                      <strong>
                        Study {index + 1}. {mainDirectory.name}
                      </strong>
                    </div>
                    {hasShortening && (
                      <div className="truncation-note">
                        <small>
                          <em>
                            Note: Original DICOM file names have been shortened
                            to display only the last 10 digits of the instance
                            UID.
                          </em>
                        </small>
                      </div>
                    )}
                    <div className="primary-directory-container">
                      {renderDirectories(
                        mainDirectory.children?.filter(
                          item => item.type === 'directory',
                        ) || [],
                        `${index + 1}`,
                        mainDirectory.name,
                      )}
                      {renderFiles(
                        mainDirectory.children?.filter(
                          item => item.type === 'file',
                        ) || [],
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Stack>
    </Stack>
  )
}

const getUploadDicomsToEncordLoadingSelector = createLoadingSelector([
  uploadDicomsToEncord.request,
])

const getUploadDicomsToEncordSuccessSelector = createSuccessSelector([
  uploadDicomsToEncord.success,
])

const getUploadDicomAsMP4LoadingSelector = createLoadingSelector([
  uploadDicomAsMP4.request,
])

const getUploadDicomAsMP4SuccessSelector = createSuccessSelector([
  uploadDicomAsMP4.success,
])

const getUploadDicomsToFlexViewLoadingSelector = createLoadingSelector([
  uploadDicomsToFlexView.request,
])

const getUploadDicomsToFlexViewSuccessSelector = createSuccessSelector([
  uploadDicomsToFlexView.success,
])

const mapStateToProps = (state: RootState) => ({
  isUploadingDicomAsMP4: getUploadDicomAsMP4LoadingSelector(state),
  isUploadedDicomAsMP4: getUploadDicomAsMP4SuccessSelector(state),
  isUploadingDicomsToEncord: getUploadDicomsToEncordLoadingSelector(state),
  isUploadedDicomsToEncord: getUploadDicomsToEncordSuccessSelector(state),
  isUploadingDicomsToFlexView: getUploadDicomsToFlexViewLoadingSelector(state),
  isUploadedDicomsToFlexView: getUploadDicomsToFlexViewSuccessSelector(state),
  allDoctors: getAllDoctors(state),
})

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      uploadDicomAsMP4: uploadDicomAsMP4Flow,
      editVideoCount: editVideoCountFlow,
      addVideoImportLog: addVideoImportLogFlow,
      uploadDicomsToEncord: uploadDicomsToEncordFlow,
      uploadDicomsToFlexView: uploadDicomsToFlexViewFlow,
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(KLZipFileViewer)
